import { useCallback, useEffect, useState } from 'react'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal, WalletMultiButton } from '@solana/wallet-adapter-react-ui'
import {
  bytesToBase58,
  fetchFeeClaims,
  fetchXSession,
  logoutXSession,
  postFeeLink,
  relativeTime,
  xAuthStartUrl,
  type FeeClaim,
  type XSession,
} from '../lib/launchesApi'
import { distributeCreatorFees } from '../lib/feeShare'

export function FeesPanel() {
  const { connection } = useConnection()
  const wallet = useWallet()
  const { setVisible } = useWalletModal()

  const [twitterConfigured, setTwitterConfigured] = useState(false)
  const [session, setSession] = useState<XSession>(null)
  const [linkedWallet, setLinkedWallet] = useState<string | null>(null)
  const [claims, setClaims] = useState<FeeClaim[]>([])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setError(null)
    try {
      const xs = await fetchXSession()
      setTwitterConfigured(xs.twitterConfigured)
      setSession(xs.session)
      const fc = await fetchFeeClaims(xs.session?.handle)
      setLinkedWallet(fc.linkedWallet)
      setClaims(fc.claims)
      if (fc.twitterConfigured) setTwitterConfigured(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const onLinkWallet = async () => {
    setError(null)
    setInfo(null)
    if (!session?.handle) {
      setError('Sign in with X first (or add Twitter API credentials).')
      return
    }
    if (!wallet.publicKey || !wallet.signMessage) {
      setVisible(true)
      setError('Connect a wallet that supports message signing.')
      return
    }
    const handle = session.handle
    const walletStr = wallet.publicKey.toBase58()
    const message =
      `EXFUND fee-link\nI am @${handle}\nLink wallet ${walletStr}\nIssued ${new Date().toISOString()}`
    setBusy('link')
    try {
      const sigBytes = await wallet.signMessage(
        new TextEncoder().encode(message),
      )
      const signature = bytesToBase58(sigBytes)
      const link = await postFeeLink({
        twitterHandle: handle,
        wallet: walletStr,
        message,
        signature,
      })
      setLinkedWallet(link.wallet)
      setInfo(`Linked @${handle} → ${link.wallet.slice(0, 4)}…${link.wallet.slice(-4)}`)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const onClaim = async (mint: string) => {
    setError(null)
    setInfo(null)
    if (!wallet.publicKey) {
      setVisible(true)
      return
    }
    setBusy(mint)
    try {
      const sig = await distributeCreatorFees(connection, wallet, mint)
      setInfo(`Distributed creator fees. Tx: ${sig.slice(0, 16)}…`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const onLogout = async () => {
    await logoutXSession()
    setSession(null)
    setClaims([])
    setLinkedWallet(null)
  }

  return (
    <div className="fees-panel">
      <div className="fees-hero">
        <h1>Claim fee share</h1>
        <p>
          Bags-style flow: creators can allocate a % of creator fees to an X
          handle at launch. Recipients sign in with X, link a Solana wallet, and
          claim when they are an on-chain shareholder.
        </p>
      </div>

      {!twitterConfigured && (
        <div className="alert warn" role="status">
          <strong>Add Twitter API credentials</strong> to enable Sign in with X.
          Create <code>api/config.local.php</code> with{' '}
          <code>TWITTER_CLIENT_ID</code> / <code>TWITTER_CLIENT_SECRET</code>{' '}
          (see <code>FEES_X.md</code>). Until then, the X button stays disabled —
          you can still connect a wallet and claim if you are already an
          on-chain shareholder for a mint.
        </div>
      )}

      <div className="fees-steps">
        <div className="fees-card">
          <h3>1. Sign in with X</h3>
          {session ? (
            <div className="fees-session">
              <span className="fees-handle">@{session.handle}</span>
              <button
                type="button"
                className="btn ghost small"
                onClick={() => void onLogout()}
              >
                Sign out
              </button>
            </div>
          ) : (
            <a
              className={`btn primary${twitterConfigured ? '' : ' disabled'}`}
              href={twitterConfigured ? xAuthStartUrl() : undefined}
              aria-disabled={!twitterConfigured}
              onClick={(e) => {
                if (!twitterConfigured) e.preventDefault()
              }}
            >
              Sign in with X
            </a>
          )}
        </div>

        <div className="fees-card">
          <h3>2. Link Solana wallet</h3>
          {!wallet.connected ? (
            <div className="lime-wallet-cta-wrap fees-wallet">
              <WalletMultiButton>Connect wallet</WalletMultiButton>
            </div>
          ) : (
            <p className="muted fineprint">
              Connected: <code>{wallet.publicKey?.toBase58()}</code>
            </p>
          )}
          {linkedWallet ? (
            <p className="alert success" style={{ marginTop: '0.75rem' }}>
              Linked wallet: <code>{linkedWallet}</code>
            </p>
          ) : (
            <button
              type="button"
              className="btn ghost"
              style={{ marginTop: '0.75rem' }}
              disabled={!session || !wallet.connected || busy === 'link'}
              onClick={() => void onLinkWallet()}
            >
              {busy === 'link' ? 'Signing…' : 'Sign message to link wallet'}
            </button>
          )}
        </div>
      </div>

      <section className="fees-claims">
        <div className="graduated-head">
          <h2>Your fee shares</h2>
          <span className="count">{claims.length}</span>
        </div>
        <p className="graduated-sub">
          Launches that named your @handle as a fee recipient. On-chain claim
          works when your wallet is a shareholder. Pending shares show until
          the recipient links before launch (v1: on-chain share applies when
          linked before create).
        </p>

        {claims.length === 0 ? (
          <div className="empty-launches">
            <h3>No fee shares yet</h3>
            <p>
              When someone launches with your @handle in fee share — and your
              wallet was linked beforehand — you’ll see it here.
            </p>
          </div>
        ) : (
          <div className="fees-claim-list">
            {claims.map((c) => (
              <article className="fees-claim-row" key={c.mint}>
                <div className="fees-claim-meta">
                  {c.imageUri ? (
                    <img src={c.imageUri} alt="" width={44} height={44} />
                  ) : (
                    <div className="token-placeholder sm">
                      {c.symbol.slice(0, 2)}
                    </div>
                  )}
                  <div>
                    <strong>
                      {c.name}{' '}
                      <span className="muted">${c.symbol}</span>
                    </strong>
                    <div className="field-note">
                      {((c.twitterFeeBps ?? 0) / 100).toFixed(0)}% of creator
                      fees · {c.feeShareStatus || 'pending'} ·{' '}
                      {relativeTime(c.createdAt)}
                    </div>
                  </div>
                </div>
                <div className="fees-claim-actions">
                  <a
                    className="btn ghost small"
                    href={c.pumpUrl || `https://pump.fun/coin/${c.mint}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    pump.fun
                  </a>
                  <button
                    type="button"
                    className="btn primary small"
                    disabled={busy === c.mint || !wallet.connected}
                    onClick={() => void onClaim(c.mint)}
                  >
                    {busy === c.mint ? 'Claiming…' : 'Claim'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {error && (
        <div className="alert error" role="alert">
          {error}
        </div>
      )}
      {info && (
        <div className="alert success" role="status">
          {info}
        </div>
      )}
    </div>
  )
}
